/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography2 } from '@coursera/cds-core';

const styles = {
  menuIconContainer: css`
    display: flex;
    align-items: center;
    width: 28px;
    height: 28px;
  `,
  buttonFocus: css`
    &:focus-visible {
      outline: 2px solid #79f;
      outline-offset: -1px;
    }
  `,
  menuItem: css`
    && {
      background-color: transparent;
      width: 100%;
      display: flex;
      align-items: center;
      gap: var(--cds-spacing-150);
      padding: var(--cds-spacing-100) var(--cds-spacing-200);

      &:hover {
        background-color: rgb(255 255 255 / 12%);
        color: var(--cds-color-neutral-primary-invert);
      }
    }
  `,
  menuItemTitle: css`
    flex: 1;
    display: flex;
    justify-content: flex-start;
    text-align: left;
  `,
  lastMenuItem: css`
    padding-bottom: 0;
  `,
};

type MenuItem = {
  title: string;
  icon?: JSX.Element;
  action?: () => void;
  actionComponent?: JSX.Element;
  isSelected?: boolean;
  buttonWrapper?: boolean;
  role?: string;
};

type PropsFromCaller = {
  menuItem: MenuItem;
  isLastItem?: boolean;
};

const ModernSettingsMenuItem = ({ menuItem, isLastItem }: PropsFromCaller) => {
  const { isSelected, title, icon, action, actionComponent, buttonWrapper = true, role = 'menuitem' } = menuItem;
  const children = (
    <>
      <div css={styles.menuIconContainer}>{icon}</div>
      <Typography2 css={styles.menuItemTitle} variant="actionPrimary" color="invertBody" component="div">
        {title}
      </Typography2>
      {actionComponent && <div>{actionComponent}</div>}
    </>
  );

  if (buttonWrapper) {
    return (
      <button
        role={role}
        {...(role === 'menuitemradio' && isSelected !== undefined ? { 'aria-checked': isSelected } : {})}
        key={title}
        type="button"
        css={[styles.menuItem, isLastItem && styles.lastMenuItem, styles.buttonFocus]}
        onClick={action}
      >
        {children}
      </button>
    );
  }

  return (
    <div key={title} css={[styles.menuItem, isLastItem && styles.lastMenuItem]}>
      {children}
    </div>
  );
};

export default ModernSettingsMenuItem;
